import { useState, useEffect } from 'react';
import { profileAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import './ProfilePopup.css';

export default function ProfilePopup({ userId, onClose, position }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [friendStatus, setFriendStatus] = useState('none'); // none, pending, friends

  const isOwnProfile = user?._id === userId;

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getUser(userId);
      setProfile(response.data.user);
      setFriendStatus(response.data.friendStatus || 'none');
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFriend = async () => {
    setActionLoading(true);
    setActionMessage('');
    try {
      await profileAPI.sendFriendRequest(userId);
      setFriendStatus('pending');
      setActionMessage('Friend request sent!');
    } catch (err) {
      setActionMessage(err.response?.data?.error || 'Failed to send request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (!confirm('Remove this friend?')) return;

    setActionLoading(true);
    setActionMessage('');
    try {
      await profileAPI.removeFriend(userId);
      setFriendStatus('none');
      setActionMessage('Friend removed');
    } catch (err) {
      setActionMessage(err.response?.data?.error || 'Failed to remove friend');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!confirm('Block this user? They won\'t be able to see your profile or message you.')) return;

    setActionLoading(true);
    setActionMessage('');
    try {
      await profileAPI.blockUser(userId);
      setActionMessage('User blocked');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setActionMessage(err.response?.data?.error || 'Failed to block user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMessage = () => {
    // For now, just show a placeholder - full DM UI would be in future
    setActionMessage('Private messaging coming soon!');
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="profile-popup" style={position ? { top: position.y, left: position.x } : {}}>
        <div className="profile-popup-loading">
          <div className="spinner-small"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-popup" style={position ? { top: position.y, left: position.x } : {}}>
        <div className="profile-popup-error">
          <p>{error || 'Profile not found'}</p>
          <button className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-popup-overlay" onClick={onClose}>
      <div
        className="profile-popup"
        onClick={(e) => e.stopPropagation()}
        style={position ? { top: position.y, left: position.x } : {}}
      >
        {/* Background Photo */}
        <div
          className="profile-background"
          style={profile.profile?.backgroundPhoto ? {
            backgroundImage: `url(${profile.profile.backgroundPhoto})`
          } : {}}
        />

        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar">
            {profile.profile?.profilePhoto ? (
              <img src={profile.profile.profilePhoto} alt={profile.username} />
            ) : (
              <span className="avatar-initials">{getInitials(profile.username)}</span>
            )}
            <span className={`online-status ${profile.isOnline ? 'online' : 'offline'}`} />
          </div>
          <div className="profile-identity">
            <h3 className="profile-username">{profile.username}</h3>
            {profile.isOnline ? (
              <span className="status-text online">Online</span>
            ) : (
              <span className="status-text offline">Offline</span>
            )}
          </div>
          <button className="close-popup-btn" onClick={onClose}>×</button>
        </div>

        {/* Bio */}
        {profile.profile?.bio && (
          <div className="profile-bio">
            <p>{profile.profile.bio}</p>
          </div>
        )}

        {/* Friends Count (if not private) */}
        {!profile.friendsListPrivate && profile.friends && (
          <div className="profile-stats">
            <span className="stat-item">
              <strong>{profile.friends.length}</strong> Friends
            </span>
          </div>
        )}

        {/* Action Message */}
        {actionMessage && (
          <div className={`action-message ${actionMessage.includes('Failed') || actionMessage.includes('blocked') ? 'error' : 'success'}`}>
            {actionMessage}
          </div>
        )}

        {/* Actions (only for other users) */}
        {!isOwnProfile && (
          <div className="profile-actions">
            {friendStatus === 'none' && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddFriend}
                disabled={actionLoading}
              >
                Add Friend
              </button>
            )}

            {friendStatus === 'pending' && (
              <button className="btn btn-secondary btn-sm" disabled>
                Request Pending
              </button>
            )}

            {friendStatus === 'friends' && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleRemoveFriend}
                disabled={actionLoading}
              >
                Remove Friend
              </button>
            )}

            <button
              className="btn btn-secondary btn-sm"
              onClick={handleMessage}
              disabled={actionLoading}
            >
              Message
            </button>

            <button
              className="btn btn-danger btn-sm"
              onClick={handleBlock}
              disabled={actionLoading}
            >
              Block
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
